import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import axios from 'axios';
import logger from '../utils/logger';

export const AuthContext = createContext();

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Add a debounce utility at the top of the file
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const subscriptionStatusRef = useRef({ data: null, timestamp: 0 });

  // Fetch subscription status from API
  const refreshSubscriptionStatus = useCallback(async (token, forceRefresh = false) => {
    if (!token) return;

    setSubscriptionLoading(true);
    try {
      // Check if we already have a recent status (less than 10 seconds old)
      // Skip cache if forceRefresh is true (useful for pricing page)
      const lastRefreshTime = subscriptionStatusRef.current?.timestamp || 0;
      if (!forceRefresh && Date.now() - lastRefreshTime < 10000 && subscriptionStatusRef.current?.data) {
        logger.debug('Using cached subscription status');
        setSubscriptionLoading(false);
        return subscriptionStatusRef.current.data;
      }

      logger.debug('Fetching fresh subscription status');
      const response = await axios.get(
        `${apiBaseUrl}/api/pricing/subscription/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.status === 'success') {
        // Store status with timestamp
        const statusData = response.data.data;

        // Log detailed subscription information for debugging
        logger.debug('Subscription status details:', {
          active: statusData.active,
          plan: statusData.plan,
          trialActive: statusData.trialActive,
          trialEnded: statusData.trialEnded,
          trialEndDate: statusData.trialEndDate,
          status: statusData.status
        });

        subscriptionStatusRef.current = {
          data: statusData,
          timestamp: Date.now()
        };
        setSubscriptionStatus(statusData);
        setSubscriptionLoading(false);
        return statusData;
      } else {
        // Set default free plan status if no data
        const defaultStatus = {
          active: false,
          plan: 'Free',
          limits: {
            agentLimit: 1,
            queryLimit: 10,
            documentLimit: 5
          }
        };
        setSubscriptionStatus(defaultStatus);
        return defaultStatus;
      }
    } catch (error) {
      logger.error('Error fetching subscription status:', error);
      // Set default free plan on error
      const defaultStatus = {
        active: false,
        plan: 'Free',
        limits: {
          agentLimit: 1,
          queryLimit: 10,
          documentLimit: 5
        }
      };
      setSubscriptionStatus(defaultStatus);
      return defaultStatus;
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  // Add a debouncedRefresh version that can be used for frequent calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedRefresh = useCallback(
    // Use the debounce utility function we defined
    debounce((token) => {
      refreshSubscriptionStatus(token);
    }, 1000),
    [refreshSubscriptionStatus]
  );

  // Log out the user
  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setSubscriptionStatus(null);
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  }, []);

  // Check subscription and redirect to pricing if needed
  const checkAndRedirectSubscription = useCallback(async (navigate, excludePaths = ['/pricing', '/login', '/signup', '/', '/verify-email']) => {
    if (!session || !navigate) return false;

    // Get current pathname
    const currentPath = window.location.pathname;

    // Don't redirect if we're already on an excluded path
    if (excludePaths.some(path => {
      // Check for exact match or if it's a parent path (like /pricing is parent of /pricing/details)
      return currentPath === path || currentPath.startsWith(`${path}/`);
    })) {
      return true;
    }

    // Check if the session might be expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (session.expires_at && currentTime >= session.expires_at) {
      logger.warn('Session appears to be expired, redirecting to login');
      await logout();
      navigate('/login', { state: { from: currentPath } });
      return false;
    }

    try {
      // Set a flag to prevent multiple redirects in case of race conditions
      if (window.isRedirectingForSubscription) {
        return false;
      }

      window.isRedirectingForSubscription = true;

      // Get fresh subscription status
      const status = await refreshSubscriptionStatus(session.access_token);

      // Reset the flag
      window.isRedirectingForSubscription = false;

      // If no active subscription, redirect to pricing page
      if (!status.active) {
        logger.info('No active subscription found, redirecting to pricing page');
        navigate('/pricing', { state: { from: currentPath } });
        return false;
      }
      return true;
    } catch (error) {
      // Reset the flag
      window.isRedirectingForSubscription = false;

      logger.error('Error checking subscription:', error);

      // Check if this is an authentication error
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        logger.warn('Authentication error during subscription check, redirecting to login');
        await logout();
        navigate('/login', { state: { from: currentPath } });
      }

      return false;
    }
  }, [session, refreshSubscriptionStatus, logout]);

  // Start a free trial subscription
  const startFreeTrial = useCallback(async () => {
    if (!session?.access_token) return { success: false, message: 'Not authenticated' };

    try {
      // First create Square customer if needed
      let customerId;
      try {
        const customerResponse = await axios.post(
          `${apiBaseUrl}/api/pricing/payment/customer`,
          {
            email: session.user.email,
            firstName: session.user.user_metadata?.first_name || '',
            lastName: session.user.user_metadata?.last_name || ''
          },
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          }
        );
        customerId = customerResponse.data.customer.id;
      } catch (error) {
        logger.error('Error creating Square customer:', error);
        return {
          success: false,
          message: 'Failed to create customer profile'
        };
      }

      // Then create subscription with free trial
      const response = await axios.post(
        `${apiBaseUrl}/api/pricing/subscribe`,
        {
          plan_id: 'free',
          source_id: 'free_trial',
          customer_id: customerId
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }
      );

      if (response.data && response.data.status === 'success') {
        // Refresh subscription status after successfully starting free trial
        await refreshSubscriptionStatus(session.access_token);
        return { success: true, data: response.data.data };
      }

      return { success: false, message: 'Failed to start free trial' };
    } catch (error) {
      logger.error('Error starting free trial:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to start free trial'
      };
    }
  }, [session, refreshSubscriptionStatus]);

  // Initialize the auth state
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setSession(session);
          setUser(session.user);
          // Fetch subscription status after auth
          refreshSubscriptionStatus(session.access_token);
        }
      } catch (error) {
        logger.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user || null);

      // Refresh subscription status on auth change
      if (newSession) {
        debouncedRefresh(newSession.access_token);
      } else {
        // Clear subscription status on logout
        setSubscriptionStatus(null);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [refreshSubscriptionStatus, debouncedRefresh]);

  // Cancel current subscription
  const cancelSubscription = useCallback(async () => {
    if (!session?.access_token || !subscriptionStatus?.id) return null;

    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/pricing/subscription/cancel`,
        { subscriptionId: subscriptionStatus.id },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }
      );

      if (response.data && response.data.status === 'success') {
        // Refresh subscription status after cancellation
        await refreshSubscriptionStatus(session.access_token);
        return response.data;
      }
      return null;
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      throw error;
    }
  }, [session, subscriptionStatus, refreshSubscriptionStatus]);

  // Simple flag to check authentication
  const isAuthenticated = !!user;

  // Check if user has a premium subscription
  const hasPremiumSubscription = subscriptionStatus?.active === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        supabase,
        logout,
        isAuthenticated,
        subscriptionStatus,
        subscriptionLoading,
        refreshSubscriptionStatus,
        cancelSubscription,
        hasPremiumSubscription,
        startFreeTrial,
        checkAndRedirectSubscription,
        debouncedRefresh
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Add this function to get user usage statistics
export const getUserUsageStats = async (session) => {
  try {
    if (!session?.access_token) {
      console.error('No valid session found for getting usage stats');
      return null;
    }

    const response = await axios.get(`${apiBaseUrl}/api/user/usage-stats`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (response.data?.status === 'success') {
      return response.data.data;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching usage statistics:', error);
    return null;
  }
};

// Add this function to check if a user is approaching or has exceeded limits
export const checkResourceLimits = async (session, resourceType) => {
  try {
    if (!session?.access_token) {
      return { 
        allowed: false, 
        error: 'Not authenticated',
        handled: true  // Flag to show we handled this error
      };
    }

    // Get the latest usage stats
    const usageStats = await getUserUsageStats(session);
    if (!usageStats) {
      return { 
        allowed: true, 
        warning: 'Unable to check limits',
        handled: true
      };
    }
    
    let current = 0;
    let limit = 0;
    
    // Get relevant limits based on resource type
    switch(resourceType) {
      case 'agent':
        current = usageStats.agents.current;
        limit = usageStats.agents.limit;
        break;
      case 'query':
        current = usageStats.queries.current;
        limit = usageStats.queries.limit;
        break;
      case 'document':
        current = usageStats.documents.current;
        limit = usageStats.documents.limit;
        break;
      default:
        return { allowed: true };
    }
    
    // Check if at or over limit
    if (current >= limit) {
      return {
        allowed: false,
        error: `You have reached your ${resourceType} limit (${current}/${limit})`,
        current,
        limit,
        usagePercent: Math.round((current / limit) * 100),
        upgradeUrl: '/pricing'
      };
    }
    
    // Success case
    return { 
      allowed: true,
      current,
      limit
    };
  } catch (error) {
    console.error('Error checking resource limits:', error);
    // Return a controlled error response
    return { 
      allowed: true, // Allow operation on error to prevent blocking users
      warning: 'Unable to check limits', 
      error: error.message,
      handled: true
    };
  }
};
